import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
});

// Request interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Add correlation ID
  config.headers['X-Correlation-ID'] = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();
  return config;
}, (error) => {
  return Promise.reject(error);
});

// The refresh token is single-use, so concurrent 401s must share one refresh call.
let refreshing = null;
const refreshTokens = () => {
  if (!refreshing) {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return Promise.reject(new Error('No refresh token'));
    }
    refreshing = axios
      .post(`${api.defaults.baseURL}/auth/refresh`, null, {
        headers: { Authorization: `Bearer ${refreshToken}`, Accept: 'application/json' }
      })
      .then((res) => {
        localStorage.setItem('token', res.data.access_token);
        localStorage.setItem('refreshToken', res.data.refresh_token);
        return res.data.access_token;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
};

// Response interceptor
api.interceptors.response.use((response) => {
  return response;
}, async (error) => {
  const originalRequest = error.config;
  const status = error.response?.status;
  const code = error.response?.data?.code;

  // MFA gate: tell the auth context which step is needed.
  if (status === 403 && (code === 'mfa_required' || code === 'mfa_setup_required')) {
    window.dispatchEvent(new CustomEvent('auth:mfa', { detail: code === 'mfa_required' ? 'verify' : 'setup' }));
    return Promise.reject(error);
  }

  if (status === 401 && originalRequest && !originalRequest._retry) {
    originalRequest._retry = true;
    try {
      const accessToken = await refreshTokens();
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return api(originalRequest);
    } catch (err) {
      const fromLink = localStorage.getItem('sessionKind') === 'link';
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionKind');
      window.location.href = fromLink ? '/login?expired=link' : '/login';
      return Promise.reject(err);
    }
  }
  return Promise.reject(error);
});

export default api;
